import {
  Close,
  NavigateBeforeRounded,
  NavigateNextRounded,
} from "@mui/icons-material";
import {
  Dialog,
  dialogClasses,
  DialogContent,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useState } from "react";
import {
  MaterialImage,
  MaterialImageProps,
} from "@/commonComponents/material-components";
import { H5_1 } from "@/styles/theme/components/typography.fontvariant";
import EnhancedSwiper from "@/commonComponents/enhanced-swiper";
import { FreeMode, Thumbs } from "swiper/modules";
import { Swiper } from "swiper/types";
import { sxArrayUtil } from "@/utils/sx-helpers";
import BorderedIconButton, {
  iconButtonLargeSx,
} from "@/commonComponents/bordered-icon-button";
import { StaticImageData } from "next/image";

type GalleryDialogProps = {
  open: boolean;
  setOpen: (d: boolean) => void;
  data: LeaderSlideProps[];
  activeIndex: number;
  fullImage?: boolean;
  MainImageProps?: Partial<MaterialImageProps>;
  MiniSwiperWrapperProps?: SxProps<Theme>;
};

export type LeaderSlideProps = {
  title?: string;
  designation?: string;
  image: string | StaticImageData;
  quote?: string;
  biography?: string;
  subTitle?: string;
};

const GalleryDialog = (props: GalleryDialogProps) => {
  const {
    open,
    setOpen,
    data,
    activeIndex,
    fullImage,
    MainImageProps,
    MiniSwiperWrapperProps,
  } = props;
  const [topSwiper, setTopSwiper] = useState<Swiper | null>(null);
  const [thumbsSwiper, setThumbsSwiper] = useState<Swiper | null>(null);

  const fullScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  const imageData = data.map((item) => ({ image: item.image }));

  const imageBox = (props: { image: string }) => {
    const { image } = props;
    return (
      <MaterialImage
        src={image}
        alt=""
        width={106}
        height={106}
        sx={{
          width: { xs: 72, md: "100%" },
          height: { xs: 72, md: "100%" },
          objectFit: "cover",
          cursor: "pointer",
        }}
      />
    );
  };

  const LeaderSlide = (props: LeaderSlideProps) => {
    const { title, designation, image, quote, biography, subTitle } = props;
    const matches = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
    return (
      <>
        <Stack
          width={1}
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "none", md: "center" }}
          rowGap={4}
          bgcolor={"common.white"}
          py={{ xs: fullImage ? 0 : 5, md: 0 }}
          px={{ xs: fullImage ? 0 : 3, md: 0 }}
          color={"primary.main"}
        >
          <MaterialImage
            src={image}
            alt=""
            width={450}
            height={533}
            sx={{
              width: { xs: "100%", md: 450 },
              height: { xs: 254, md: 533 },
              objectFit: "cover",
            }}
            {...MainImageProps}
          />
          {!fullImage && (
            <Stack px={{ xs: 0, md: 2, md_lg: 7 }} rowGap={3}>
              {quote && (
                <Typography variant="h3" fontSize={H5_1}>
                  {quote}
                </Typography>
              )}
              <Stack rowGap={0.5}>
                <Typography fontSize={"18px !important"}>{title}</Typography>
                <Typography variant="body2">{designation}</Typography>
              </Stack>
              <Typography
                fontSize={"16px !important"}
                sx={{ maxHeight: 390, overflow: "scroll", opacity: 0.7 }}
              >
                {biography}
              </Typography>
            </Stack>
          )}
        </Stack>
        {subTitle && matches && (
          <Typography mt={2} color={"common.white"} variant={"subtitle2"}>
            {subTitle}
          </Typography>
        )}
      </>
    );
  };
  return (
    <Dialog
      onClose={() => setOpen(false)}
      open={open}
      fullWidth
      fullScreen={fullImage ? false : fullScreen}
      maxWidth={"lg"}
      sx={{
        [`.${dialogClasses.paper}`]: {
          borderRadius: 0,
          m: 3,
          width: 1,
          backgroundColor: "transparent",
          boxShadow: "none",
          minHeight: fullImage ? 615 : "auto",
          overflow: fullImage ? { xs: "hidden", xl: "visible" } : "hidden",
        },
      }}
      scroll="paper"
    >
      <DialogContent
        sx={{
          width: 1,
          p: 0,
          overflow: fullImage ? { xs: "hidden", xl: "visible" } : "auto",
        }}
      >
        <IconButton
          onClick={() => setOpen(false)}
          sx={{
            position: fullImage ? "fixed" : "absolute",
            top: fullImage ? 35 : { xs: 2, md: 10, lg: 20 },
            right: fullImage ? 18 : { xs: 8, md: 10, lg: 20 },
            zIndex: 2,
          }}
        >
          <Close sx={{ color: fullImage ? "common.white" : "primary.main" }} />
        </IconButton>
        <EnhancedSwiper
          data={data}
          SlideComponent={LeaderSlide}
          loop={true}
          initialSlide={activeIndex}
          thumbs={{
            swiper:
              thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
          }}
          onSwiper={setTopSwiper}
          modules={[Thumbs]}
          sx={{
            height: "auto",
          }}
        />
        <Stack
          direction="row"
          alignItems={"center"}
          width={fullImage ? { xs: 1, xl: "calc(100% + 176px)" } : 1}
          ml={fullImage ? { xs: 0, xl: -11 } : 0}
          columnGap={fullImage ? { xs: 2, xl: 5 } : 2}
          pt={3}
        >
          <BorderedIconButton
            onClick={() => {
              topSwiper && topSwiper.slidePrev();
            }}
            sx={[
              iconButtonLargeSx,
              ...sxArrayUtil({ display: { xs: "none", md: "flex" } }),
            ]}
          >
            <NavigateBeforeRounded />
          </BorderedIconButton>
          <EnhancedSwiper
            data={imageData}
            SlideComponent={imageBox}
            onSwiper={setThumbsSwiper}
            // loop={true}
            spaceBetween={16}
            slidesPerView={"auto"}
            freeMode={true}
            watchSlidesProgress={true}
            modules={[FreeMode]}
            SlideWrapperProps={{
              sx: {
                width: { xs: 72, md: 106 },
                height: { xs: 72, md: 106 },
              },
            }}
            sx={[
              {
                // mt: { xs: "-160px", md: "-100px" },
                width: 1,
                height: "auto",
                ".swiper-wrapper": {
                  justifyContent: {
                    xs: "flex-start",
                    sm: "center",
                    md: "flex-start",
                    md_lg: "center",
                  },
                },
                ".swiper-slide-thumb-active": {
                  border: "1.5px solid #fff",
                },
              },
              ...sxArrayUtil(MiniSwiperWrapperProps),
            ]}
          />
          <BorderedIconButton
            onClick={() => {
              topSwiper && topSwiper.slideNext();
            }}
            sx={[
              iconButtonLargeSx,
              ...sxArrayUtil({ display: { xs: "none", md: "flex" } }),
            ]}
          >
            <NavigateNextRounded />
          </BorderedIconButton>
        </Stack>
        <Stack
          direction="row"
          mt={4}
          justifyContent={"center"}
          gap={2}
          display={{ xs: "flex", md: "none" }}
        >
          <BorderedIconButton
            onClick={() => {
              topSwiper && topSwiper.slidePrev();
            }}
            sx={iconButtonLargeSx}
          >
            <NavigateBeforeRounded />
          </BorderedIconButton>
          <BorderedIconButton
            onClick={() => {
              topSwiper && topSwiper.slideNext();
            }}
            sx={iconButtonLargeSx}
          >
            <NavigateNextRounded />
          </BorderedIconButton>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default GalleryDialog;
